// src/components/general/skeleton/Skeleton.jsx
import React from 'react';
import '../../../assets/css/components/general/skeleton/SkeletonBlock.css';


// ─── Base Skeleton Block ───────────────────────────────
export const SkeletonBlock = ({ width = '100%', height = '20px', borderRadius = '4px', className = '' }) => (
  <div
    className={`skel-block ${className}`}
    style={{ width, height, borderRadius }}
  />
);

// ─── Skeleton Circle ──────────────────────────────────
export const SkeletonCircle = ({ size = '50px', className = '' }) => (
  <div
    className={`skel-block ${className}`}
    style={{ width: size, height: size, borderRadius: '50%', flexShrink: 0 }}
  />
);

// ─── Hero Section Skeleton ────────────────────────────
export const HeroSkeleton = () => (
  <div className="skel-hero">
    <div className="skel-hero-bg skel-block" />
    <div className="skel-hero-content">
      <SkeletonBlock width="60%" height="clamp(2rem,5vw,5rem)" borderRadius="8px" />
      <SkeletonBlock width="40%" height="clamp(1.5rem,3vw,3rem)" borderRadius="8px" className="skel-mt" />
    </div>
    {/* Feature cards */}
    <div className="skel-hero-cards">
      {[1, 2, 3].map(i => (
        <div key={i} className="skel-feature-card">
          <SkeletonCircle size="55px" />
          <div className="skel-feature-text">
            <SkeletonBlock width="70%" height="16px" borderRadius="4px" />
            <SkeletonBlock width="90%" height="13px" borderRadius="4px" className="skel-mt-sm" />
          </div>
        </div>
      ))}
    </div>
  </div>
);

// ─── Category Card Skeleton ───────────────────────────
export const CategoryCardSkeleton = () => (
  <div className="skel-cat-card">
    <SkeletonCircle size="clamp(100px,18vw,145px)" />
    <SkeletonBlock width="70%" height="14px" borderRadius="4px" className="skel-mt-sm" />
  </div>
);

// ─── Categories Section Skeleton ─────────────────────
export const CategoriesSkeleton = ({ count = 4 }) => (
  <div className="skel-categories">
    <div className="skel-section-header">
      <SkeletonBlock width="220px" height="clamp(1.5rem,3vw,2.5rem)" borderRadius="6px" className="skel-center" />
      <SkeletonBlock width="300px" height="14px" borderRadius="4px" className="skel-mt-sm skel-center" />
      <div className="skel-cat-btns">
        <SkeletonBlock width="120px" height="36px" borderRadius="50px" />
        <SkeletonBlock width="120px" height="36px" borderRadius="50px" />
      </div>
    </div>
    <div className="skel-cat-grid">
      {Array.from({ length: count }).map((_, i) => (
        <CategoryCardSkeleton key={i} />
      ))}
    </div>
  </div>
);

// ─── Product Card Skeleton ────────────────────────────
export const ProductCardSkeleton = () => (
  <div className="skel-product-card">
    <SkeletonBlock width="100%" height="200px" borderRadius="12px 12px 0 0" />
    <div className="skel-product-info">
      <SkeletonBlock width="90%" height="16px" borderRadius="4px" />
      <SkeletonBlock width="50%" height="14px" borderRadius="4px" className="skel-mt-sm" />
      <SkeletonBlock width="40%" height="20px" borderRadius="4px" className="skel-mt-sm" />
      <SkeletonBlock width="100%" height="36px" borderRadius="50px" className="skel-mt" />
    </div>
  </div>
);

// ─── New Arrivals / Products Section Skeleton ─────────
export const ProductsSkeleton = ({ count = 4 }) => (
  <div className="skel-products-section">
    <div className="skel-section-header">
      <SkeletonBlock width="180px" height="clamp(1.5rem,3vw,2.5rem)" borderRadius="6px" className="skel-center" />
      <SkeletonBlock width="280px" height="14px" borderRadius="4px" className="skel-mt-sm skel-center" />
    </div>
    <div className="skel-products-grid">
      {Array.from({ length: count }).map((_, i) => (
        <ProductCardSkeleton key={i} />
      ))}
    </div>
  </div>
);

// ─── About Us Skeleton ────────────────────────────────
export const AboutUsSkeleton = () => (
  <div className="skel-about">
    {/* Left */}
    <div className="skel-about-left">
      <SkeletonBlock width="160px" height="36px" borderRadius="50px" />
      <SkeletonBlock width="80%" height="clamp(1.75rem,4vw,3rem)" borderRadius="8px" className="skel-mt" />
      <SkeletonBlock width="90%" height="14px" borderRadius="4px" className="skel-mt-sm" />
      <SkeletonBlock width="85%" height="14px" borderRadius="4px" className="skel-mt-sm" />
      <SkeletonBlock width="80%" height="14px" borderRadius="4px" className="skel-mt-sm" />
      {[1, 2, 3].map(i => (
        <div key={i} className="skel-feature-row skel-mt">
          <SkeletonCircle size="55px" />
          <div className="skel-feature-text">
            <SkeletonBlock width="60%" height="16px" borderRadius="4px" />
            <SkeletonBlock width="80%" height="13px" borderRadius="4px" className="skel-mt-sm" />
          </div>
        </div>
      ))}
      <SkeletonBlock width="140px" height="42px" borderRadius="50px" className="skel-mt" />
    </div>
    {/* Right */}
    <div className="skel-about-right">
      <SkeletonBlock width="100%" height="clamp(300px,42vw,450px)" borderRadius="200px 200px 12px 12px" />
      <div className="skel-stat-row skel-mt">
        {[1, 2, 3].map(i => (
          <div key={i} className="skel-stat-card">
            <SkeletonBlock width="80px" height="36px" borderRadius="6px" className="skel-center" />
            <SkeletonBlock width="100%" height="13px" borderRadius="4px" className="skel-mt-sm" />
          </div>
        ))}
      </div>
    </div>
  </div>
);

// ─── Promo Cards Skeleton ─────────────────────────────
export const PromoCardsSkeleton = ({ count = 3 }) => (
  <div className="skel-promo-grid">
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="skel-promo-card">
        <SkeletonBlock width="100%" height="300px" borderRadius="20px" />
      </div>
    ))}
  </div>
);

// ─── Testimonial Skeleton ─────────────────────────────
export const TestimonialsSkeleton = () => (
  <div className="skel-testimonials">
    {/* Left images */}
    <div className="skel-test-images">
      <SkeletonBlock width="160px" height="220px" borderRadius="15px" className="skel-test-side" />
      <SkeletonBlock width="200px" height="300px" borderRadius="15px" className="skel-test-center" />
      <SkeletonBlock width="160px" height="220px" borderRadius="15px" className="skel-test-side" />
    </div>
    {/* Right content */}
    <div className="skel-test-content">
      <SkeletonBlock width="150px" height="20px" borderRadius="4px" />
      <SkeletonBlock width="70%" height="clamp(1.75rem,4vw,3rem)" borderRadius="8px" className="skel-mt" />
      <SkeletonBlock width="90%" height="14px" borderRadius="4px" className="skel-mt" />
      <SkeletonBlock width="85%" height="14px" borderRadius="4px" className="skel-mt-sm" />
      <SkeletonBlock width="80%" height="14px" borderRadius="4px" className="skel-mt-sm" />
      <div className="skel-test-author skel-mt">
        <SkeletonCircle size="55px" />
        <div className="skel-feature-text">
          <SkeletonBlock width="120px" height="16px" borderRadius="4px" />
          <SkeletonBlock width="90px" height="13px" borderRadius="4px" className="skel-mt-sm" />
        </div>
      </div>
    </div>
  </div>
);

// ─── Blog Card Skeleton ───────────────────────────────
export const BlogCardSkeleton = () => (
  <div className="skel-blog-card">
    <SkeletonBlock width="100%" height="200px" borderRadius="12px 12px 0 0" />
    <div className="skel-blog-info">
      <SkeletonBlock width="60%" height="13px" borderRadius="4px" />
      <SkeletonBlock width="90%" height="16px" borderRadius="4px" className="skel-mt-sm" />
      <SkeletonBlock width="75%" height="16px" borderRadius="4px" className="skel-mt-sm" />
    </div>
  </div>
);

// ─── Blog Section Skeleton ────────────────────────────
export const BlogSectionSkeleton = () => (
  <div className="skel-blog-section">
    <div className="skel-blog-header">
      <SkeletonBlock width="100px" height="16px" borderRadius="4px" />
      <SkeletonBlock width="80%" height="clamp(1.5rem,3vw,2.5rem)" borderRadius="8px" className="skel-mt" />
    </div>
    <div className="skel-blog-grid">
      {[1, 2].map(i => <BlogCardSkeleton key={i} />)}
    </div>
  </div>
);

// ─── Products Page Skeleton (sidebar + grid) ──────────
export const ProductsPageSkeleton = () => (
  <div className="skel-products-page">
    {/* Sidebar */}
    <div className="skel-sidebar">
      {[1, 2, 3].map(i => (
        <div key={i} className="skel-sidebar-widget">
          <SkeletonBlock width="60%" height="18px" borderRadius="4px" />
          <SkeletonBlock width="100%" height="40px" borderRadius="8px" className="skel-mt" />
          {i === 3 && [1,2,3,4].map(j => (
            <SkeletonBlock key={j} width="80%" height="16px" borderRadius="4px" className="skel-mt-sm" />
          ))}
        </div>
      ))}
    </div>
    {/* Main grid */}
    <div className="skel-main-content">
      <SkeletonBlock width="100%" height="60px" borderRadius="8px" />
      <div className="skel-products-grid skel-mt">
        {Array.from({ length: 6 }).map((_, i) => (
          <ProductCardSkeleton key={i} />
        ))}
      </div>
    </div>
  </div>
);

// ─── Single Product Detail Skeleton ───────────────────
export const SingleProductSkeleton = () => (
  <div className="skel-single-product">
    {/* Left - images */}
    <div className="skel-single-left">
      <SkeletonBlock width="100%" height="420px" borderRadius="20px" />
      <div className="skel-thumbnails skel-mt">
        {[1, 2].map(i => (
          <SkeletonBlock key={i} width="100px" height="100px" borderRadius="12px" />
        ))}
      </div>
    </div>
    {/* Right - info */}
    <div className="skel-single-right">
      <SkeletonBlock width="80%" height="clamp(1.5rem,3vw,2.2rem)" borderRadius="8px" />
      <SkeletonBlock width="40%" height="20px" borderRadius="4px" className="skel-mt-sm" />
      <SkeletonBlock width="30%" height="32px" borderRadius="4px" className="skel-mt" />
      <SkeletonBlock width="100%" height="14px" borderRadius="4px" className="skel-mt" />
      <SkeletonBlock width="90%" height="14px" borderRadius="4px" className="skel-mt-sm" />
      <SkeletonBlock width="95%" height="14px" borderRadius="4px" className="skel-mt-sm" />
      <div className="skel-qty-row skel-mt">
        <SkeletonBlock width="120px" height="44px" borderRadius="8px" />
        <SkeletonBlock width="180px" height="44px" borderRadius="50px" />
      </div>
      <SkeletonBlock width="60%" height="14px" borderRadius="4px" className="skel-mt" />
      <SkeletonBlock width="50%" height="14px" borderRadius="4px" className="skel-mt-sm" />
    </div>
  </div>
);

// ─── Related Products Skeleton ─────────────────────────
export const RelatedProductsSkeleton = () => (
  <div className="skel-related">
    <SkeletonBlock width="220px" height="clamp(1.5rem,3vw,2.5rem)" borderRadius="6px" className="skel-center" />
    <div className="skel-products-grid skel-mt">
      {Array.from({ length: 4 }).map((_, i) => (
        <ProductCardSkeleton key={i} />
      ))}
    </div>
  </div>

);
// ─── Shops List Skeleton (Sidebar + Shop Cards) ───────────────
export const ShopsPageSkeleton = () => (
  <div className="skel-products-page">
    {/* Sidebar */}
    <div className="skel-sidebar">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="skel-sidebar-widget">
          <SkeletonBlock width="60%" height="18px" borderRadius="4px" />
          <SkeletonBlock width="100%" height="40px" borderRadius="8px" className="skel-mt" />
          {i === 2 && [1, 2, 3, 4, 5, 6, 7, 8].map((j) => (
            <SkeletonBlock key={j} width="80%" height="16px" borderRadius="4px" className="skel-mt-sm" />
          ))}
          {i === 3 && [1, 2, 3].map((j) => (
            <SkeletonBlock key={j} width="70%" height="16px" borderRadius="4px" className="skel-mt-sm" />
          ))}
        </div>
      ))}
    </div>

    {/* Shop Cards */}
    <div className="skel-main-content">
      <SkeletonBlock width="200px" height="20px" borderRadius="4px" />
      <div className="skel-mt">
        {[1, 2, 3].map((i) => (
          <div key={i} className="skel-shop-card">
            <SkeletonBlock width="280px" height="180px" borderRadius="12px 0 0 12px" className="skel-shop-img" />
            <div className="skel-shop-info">
              <div className="skel-shop-header">
                <SkeletonBlock width="55%" height="22px" borderRadius="4px" />
                <SkeletonBlock width="24px" height="24px" borderRadius="50%" />
              </div>
              <SkeletonBlock width="120px" height="16px" borderRadius="4px" className="skel-mt-sm" />
              <SkeletonBlock width="95%" height="14px" borderRadius="4px" className="skel-mt" />
              <SkeletonBlock width="85%" height="14px" borderRadius="4px" className="skel-mt-sm" />
              <div className="skel-shop-footer skel-mt">
                <SkeletonBlock width="160px" height="14px" borderRadius="4px" />
                <SkeletonBlock width="36px" height="36px" borderRadius="50%" />
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* Pagination skeleton */}
      <div className="skel-pagination skel-mt">
        <SkeletonBlock width="180px" height="16px" borderRadius="4px" />
        <div className="skel-pagination-btns">
          {[1, 2, 3, 4, 5].map((i) => (
            <SkeletonBlock key={i} width="38px" height="38px" borderRadius="6px" />
          ))}
        </div>
        <SkeletonBlock width="120px" height="38px" borderRadius="6px" />
      </div>
    </div>
  </div>
);

// ─── Shop Detail Hero Skeleton ────────────────────────────────
export const ShopDetailSkeleton = () => (
  <div className="skel-shop-detail">
    {/* Hero Cover */}
    <SkeletonBlock width="100%" height="320px" borderRadius="0" />

    {/* Hero Content */}
    <div className="skel-shop-detail-hero">
      <SkeletonCircle size="110px" />
      <div className="skel-shop-detail-info">
        <SkeletonBlock width="280px" height="28px" borderRadius="6px" />
        <SkeletonBlock width="200px" height="16px" borderRadius="4px" className="skel-mt-sm" />
        <div className="skel-shop-detail-stats">
          <SkeletonBlock width="120px" height="20px" borderRadius="4px" />
          <SkeletonBlock width="140px" height="20px" borderRadius="4px" />
          <SkeletonBlock width="100px" height="20px" borderRadius="4px" />
        </div>
        <SkeletonBlock width="90%" height="14px" borderRadius="4px" className="skel-mt" />
        <SkeletonBlock width="80%" height="14px" borderRadius="4px" className="skel-mt-sm" />
      </div>
    </div>

    {/* Filter Bar */}
    <div className="skel-shop-filterbar">
      {[1, 2, 3, 4].map((i) => (
        <SkeletonBlock key={i} width="110px" height="40px" borderRadius="50px" />
      ))}
      <SkeletonBlock width="160px" height="40px" borderRadius="50px" />
      <SkeletonBlock width="220px" height="40px" borderRadius="50px" />
      <SkeletonBlock width="130px" height="40px" borderRadius="50px" />
    </div>

    {/* Products Grid */}
    <div className="skel-shop-detail-products">
      <SkeletonBlock width="160px" height="24px" borderRadius="6px" />
      <div className="skel-products-grid skel-mt">
        {Array.from({ length: 8 }).map((_, i) => (
          <ProductCardSkeleton key={i} />
        ))}
      </div>
    </div>
  </div>
)

// // ─── Dashboard Page Header Skeleton ──────────────────
const DashPageHeader = () => (
  <div className="skel-dash-header">
    <SkeletonBlock width="54px" height="54px" borderRadius="12px" />
    <div style={{ flex: 1 }}>
      <SkeletonBlock width="160px" height="20px" borderRadius="6px" />
      <SkeletonBlock width="100px" height="13px" borderRadius="4px" className="skel-mt-sm" />
    </div>
  </div>
);

// ─── Stats Row Skeleton ───────────────────────────────
const StatsRowSkeleton = ({ count = 4 }) => (
  <div className="skel-stats-row" style={{ '--skel-cols': count }}>
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="skel-stat-row-card">
        <div style={{ flex: 1, minWidth: 0 }}>
          <SkeletonBlock width="80px" height="13px" borderRadius="4px" />
          <SkeletonBlock width="60px" height="22px" borderRadius="6px" className="skel-mt-sm" />
        </div>
        <SkeletonBlock width="50px" height="50px" borderRadius="12px" />
      </div>
    ))}
  </div>
);

// ─── Table Card Skeleton ──────────────────────────────
const TableCardSkeleton = ({ rows = 5, cols = 6 }) => (
  <div className="skel-table-card">
    <div className="skel-table-scroll">
      {/* thead */}
      <div className="skel-table-head-row" style={{ '--skel-cols': cols }}>
        {Array.from({ length: cols }).map((_, i) => (
          <SkeletonBlock key={i} width="80%" height="14px" borderRadius="4px" />
        ))}
      </div>
      {/* rows */}
      {Array.from({ length: rows }).map((_, ri) => (
        <div key={ri} className="skel-table-body-row" style={{ '--skel-cols': cols }}>
          {Array.from({ length: cols }).map((_, ci) => (
            <SkeletonBlock key={ci} width={ci === 0 ? "90%" : ci === cols-1 ? "40px" : "70%"} height="13px" borderRadius="4px" />
          ))}
        </div>
      ))}
    </div>
    {/* pagination */}
    <div className="skel-table-pagination">
      <SkeletonBlock width="200px" height="13px" borderRadius="4px" />
      <div style={{ display: 'flex', gap: '8px' }}>
        {[1,2,3,4,5].map(i => <SkeletonBlock key={i} width="38px" height="38px" borderRadius="8px" />)}
      </div>
      <SkeletonBlock width="120px" height="36px" borderRadius="8px" />
    </div>
  </div>
);

// ─── Filter Bar Skeleton ──────────────────────────────
const FilterBarSkeleton = () => (
  <div className="skel-filter-bar">
    <SkeletonBlock width="250px" height="40px" borderRadius="8px" />
    <SkeletonBlock width="180px" height="40px" borderRadius="8px" />
  </div>
);

// ─── Card Section Skeleton ────────────────────────────
const CardSectionSkeleton = ({ rows = 3 }) => (
  <div className="skel-card-section">
    <SkeletonBlock width="200px" height="18px" borderRadius="6px" />
    <div className="skel-card-section-rows">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="skel-card-section-row">
          <SkeletonBlock width="160px" height="13px" borderRadius="4px" />
          <SkeletonBlock width="48px" height="24px" borderRadius="50px" />
        </div>
      ))}
    </div>
  </div>
);

// ─── Form Card Skeleton ───────────────────────────────
const FormCardSkeleton = ({ fields = 4 }) => (
  <div className="skel-form-card">
    <div className="skel-form-card-header">
      <SkeletonBlock width="50px" height="50px" borderRadius="12px" />
      <div style={{ flex: 1, minWidth: 0 }}>
        <SkeletonBlock width="160px" height="16px" borderRadius="6px" />
        <SkeletonBlock width="120px" height="12px" borderRadius="4px" className="skel-mt-sm" />
      </div>
      <SkeletonBlock width="80px" height="36px" borderRadius="50px" />
    </div>
    <div className="skel-form-fields-grid">
      {Array.from({ length: fields }).map((_, i) => (
        <div key={i}>
          <SkeletonBlock width="100px" height="13px" borderRadius="4px" />
          <SkeletonBlock width="100%" height="40px" borderRadius="8px" className="skel-mt-sm" />
        </div>
      ))}
      <div className="skel-form-field-full">
        <SkeletonBlock width="140px" height="40px" borderRadius="50px" className="skel-mt" />
      </div>
    </div>
  </div>
);

// ─── Sidebar Profile Skeleton ─────────────────────────
const SidebarProfileSkeleton = () => (
  <div className="skel-sidebar-profile">
    <SkeletonCircle size="120px" className="skel-center" />
    <SkeletonBlock width="60%" height="18px" borderRadius="6px" className="skel-mt skel-center" />
    <SkeletonBlock width="80%" height="13px" borderRadius="4px" className="skel-mt-sm skel-center" />
    <div style={{ marginTop: '1.5rem' }}>
      {[1,2,3,4].map(i => (
        <SkeletonBlock key={i} width="100%" height="36px" borderRadius="8px" className="skel-mt-sm" />
      ))}
    </div>
  </div>
);

// ─── FAQ Skeleton ─────────────────────────────────────
const FaqSkeleton = () => (
  <div className="skel-faq-card">
    <SkeletonBlock width="220px" height="18px" borderRadius="6px" />
    <div className="skel-faq-rows">
      {[1,2,3,4,5].map(i => (
        <div key={i} className="skel-faq-row">
          <SkeletonBlock width="70%" height="14px" borderRadius="4px" />
          <SkeletonBlock width="18px" height="18px" borderRadius="4px" />
        </div>
      ))}
    </div>
  </div>
);

// ═══════════════════════════════════════════════
//   CUSTOMER DASHBOARD PAGE SKELETONS
// ═══════════════════════════════════════════════

// ── Orders Page ──────────────────────────────────────
export const OrdersPageSkeleton = () => (
  <div style={{ padding: '0 0.5rem' }}>
    <DashPageHeader />
    <StatsRowSkeleton count={4} />
    <FilterBarSkeleton />
    <TableCardSkeleton rows={6} cols={7} />
  </div>
);

// ── Help Page ─────────────────────────────────────────
export const HelpPageSkeleton = () => (
  <div style={{ padding: '0 0.5rem' }}>
    <DashPageHeader />
    <div className="skel-two-col">
      <FormCardSkeleton fields={3} />
      <FaqSkeleton />
    </div>
    <TableCardSkeleton rows={4} cols={9} />
  </div>
);

// ── Profile Page ──────────────────────────────────────
export const ProfilePageSkeleton = () => (
  <div style={{ padding: '0 0.5rem' }}>
    <DashPageHeader />
    <div className="skel-profile-layout">
      <SidebarProfileSkeleton />
      <FormCardSkeleton fields={6} />
    </div>
  </div>
);

// ── Wishlist Page ─────────────────────────────────────
export const WishlistPageSkeleton = () => (
  <div style={{ padding: '0 0.5rem' }}>
    <DashPageHeader />
    {/* Toolbar */}
    <div style={{ background: '#fff', borderRadius: '12px', padding: '1rem 1.25rem', boxShadow: '0 2px 10px rgba(0,0,0,.06)', marginBottom: '1rem', display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
      {[1,2,3,4,5].map(i => <SkeletonBlock key={i} width="90px" height="32px" borderRadius="50px" />)}
    </div>
    {/* Grid */}
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '1.5rem' }}>
      {Array.from({ length: 6 }).map((_, i) => <ProductCardSkeleton key={i} />)}
    </div>
  </div>
);

// ── Cart Page ──────────────────────────────────────────
export const CartPageSkeleton = () => (
  <div style={{ padding: '0 0.5rem' }}>
    {/* Cart header */}
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
        <SkeletonBlock width="54px" height="54px" borderRadius="50%" />
        <div>
          <SkeletonBlock width="160px" height="22px" borderRadius="6px" />
          <SkeletonBlock width="110px" height="13px" borderRadius="4px" className="skel-mt-sm" />
        </div>
      </div>
      <SkeletonBlock width="170px" height="38px" borderRadius="50px" />
    </div>

    <div className="skel-cart-layout">
      {/* Cart items column */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {Array.from({ length: 3 }).map((_, i) => (
          <div
            key={i}
            style={{
              background: '#fff',
              borderRadius: '12px',
              padding: '1.25rem',
              boxShadow: '0 2px 10px rgba(0,0,0,.06)',
              display: 'flex',
              gap: '1rem',
              alignItems: 'flex-start',
              flexWrap: 'wrap',
            }}
          >
            <SkeletonBlock width="100px" height="100px" borderRadius="12px" />
            <div style={{ flex: 1, minWidth: '160px' }}>
              <SkeletonBlock width="70%" height="16px" borderRadius="4px" />
              <SkeletonBlock width="45%" height="13px" borderRadius="4px" className="skel-mt-sm" />
              <SkeletonBlock width="55%" height="13px" borderRadius="4px" className="skel-mt-sm" />
              <SkeletonBlock width="35%" height="18px" borderRadius="4px" className="skel-mt" />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', alignItems: 'flex-end' }}>
              <SkeletonBlock width="120px" height="36px" borderRadius="8px" />
              <SkeletonBlock width="130px" height="32px" borderRadius="50px" />
              <SkeletonBlock width="32px" height="32px" borderRadius="8px" />
            </div>
          </div>
        ))}
      </div>

      {/* Order summary column */}
      <div style={{ background: '#fff', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 2px 10px rgba(0,0,0,.06)' }}>
        <SkeletonBlock width="140px" height="18px" borderRadius="6px" />
        <div style={{ marginTop: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <SkeletonBlock width="70px" height="13px" borderRadius="4px" />
            <SkeletonBlock width="80px" height="13px" borderRadius="4px" />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <SkeletonBlock width="50px" height="13px" borderRadius="4px" />
            <SkeletonBlock width="70px" height="13px" borderRadius="4px" />
          </div>
        </div>
        <SkeletonBlock width="100%" height="1px" borderRadius="0" className="skel-mt" />
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '1rem' }}>
          <SkeletonBlock width="60px" height="18px" borderRadius="4px" />
          <SkeletonBlock width="100px" height="18px" borderRadius="4px" />
        </div>
        <SkeletonBlock width="100%" height="13px" borderRadius="4px" className="skel-mt" />
        <SkeletonBlock width="90%" height="13px" borderRadius="4px" className="skel-mt-sm" />
        <SkeletonBlock width="100%" height="44px" borderRadius="50px" className="skel-mt" />
        <SkeletonBlock width="100%" height="44px" borderRadius="50px" className="skel-mt-sm" />
      </div>
    </div>
  </div>
);

// ── Settings Page ─────────────────────────────────────
export const SettingsPageSkeleton = () => (
  <div style={{ padding: '0 0.5rem' }}>
    <DashPageHeader />
    <FormCardSkeleton fields={4} />
    <div className="skel-two-col" style={{ marginTop: '1.5rem', marginBottom: 0 }}>
      <FormCardSkeleton fields={3} />
      <CardSectionSkeleton rows={5} />
      <CardSectionSkeleton rows={3} />
      <FormCardSkeleton fields={3} />
    </div>
  </div>
);

// ── Order Details Page ────────────────────────────────
export const OrderDetailsSkeleton = () => (
  <div style={{ padding: '0 0.5rem' }}>
    <DashPageHeader />
    {/* Status bar */}
    <div style={{ background: '#fff', borderRadius: '12px', padding: '1.25rem 1.5rem', boxShadow: '0 2px 10px rgba(0,0,0,.06)', marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <SkeletonBlock width="200px" height="16px" borderRadius="6px" />
      <div style={{ display: 'flex', gap: '0.75rem' }}>
        <SkeletonBlock width="100px" height="36px" borderRadius="50px" />
        <SkeletonBlock width="100px" height="36px" borderRadius="50px" />
      </div>
    </div>
    {/* Products */}
    {[1,2].map(i => (
      <div key={i} style={{ background: '#fff', borderRadius: '12px', padding: '1.25rem', boxShadow: '0 2px 10px rgba(0,0,0,.06)', marginBottom: '1rem', display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
        <SkeletonBlock width="90px" height="90px" borderRadius="12px" />
        <div style={{ flex: 1 }}>
          <SkeletonBlock width="70%" height="16px" borderRadius="6px" />
          <SkeletonBlock width="50%" height="13px" borderRadius="4px" className="skel-mt-sm" />
          <SkeletonBlock width="30%" height="18px" borderRadius="6px" className="skel-mt" />
        </div>
        <SkeletonBlock width="100px" height="36px" borderRadius="50px" />
      </div>
    ))}
  </div>
);
// ── Notifications Page ────────────────────────────────
export const NotificationsPageSkeleton = () => (
  <div style={{ padding: '0 0.5rem' }}>
    <DashPageHeader />
    <StatsRowSkeleton count={3} />
    <FilterBarSkeleton />
    <TableCardSkeleton rows={6} cols={7} />
  </div>
);
// ── Messages Page ──────────────────────────────────────
export const MessagesPageSkeleton = () => (
  <div style={{ padding: '0 0.5rem' }}>
    <DashPageHeader />
    <div className="skel-messages-layout">
      {/* Chat list sidebar */}
      <div style={{ borderRight: '1px solid #dee2e6', padding: '1rem' }}>
        <SkeletonBlock width="100%" height="40px" borderRadius="8px" />
        <div style={{ marginTop: '1.25rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
              <SkeletonCircle size="44px" />
              <div style={{ flex: 1 }}>
                <SkeletonBlock width="70%" height="14px" borderRadius="4px" />
                <SkeletonBlock width="90%" height="12px" borderRadius="4px" className="skel-mt-sm" />
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Chat area */}
      <div style={{ padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', paddingBottom: '1rem', borderBottom: '1px solid #dee2e6' }}>
          <SkeletonCircle size="44px" />
          <SkeletonBlock width="150px" height="16px" borderRadius="4px" />
        </div>
        {[1, 2, 3, 4].map((i) => (
          <SkeletonBlock key={i} width={i % 2 === 0 ? '55%' : '40%'} height="40px" borderRadius="12px" />
        ))}
      </div>
    </div>
  </div>
);

// ─── About Us Skeleton (keep existing) ───────────────
export const AboutSkeleton = () => (
  <div className="skel-about">
    <div className="skel-about-left">
      <SkeletonBlock width="160px" height="clamp(1.5rem,3vw,2.5rem)" borderRadius="6px" />
      <SkeletonBlock width="100%" height="14px" borderRadius="4px" className="skel-mt" />
      <SkeletonBlock width="90%"  height="14px" borderRadius="4px" className="skel-mt-sm" />
      <SkeletonBlock width="95%"  height="14px" borderRadius="4px" className="skel-mt-sm" />
      <div className="skel-feature-row" style={{ marginTop: '1.5rem' }}>
        <SkeletonCircle size="48px" />
        <div style={{ flex: 1 }}>
          <SkeletonBlock width="60%" height="16px" borderRadius="4px" />
          <SkeletonBlock width="80%" height="13px" borderRadius="4px" className="skel-mt-sm" />
        </div>
      </div>
      <div className="skel-feature-row" style={{ marginTop: '1rem' }}>
        <SkeletonCircle size="48px" />
        <div style={{ flex: 1 }}>
          <SkeletonBlock width="60%" height="16px" borderRadius="4px" />
          <SkeletonBlock width="80%" height="13px" borderRadius="4px" className="skel-mt-sm" />
        </div>
      </div>
      <div className="skel-stat-row" style={{ marginTop: '1.5rem' }}>
        <div className="skel-stat-card"><SkeletonBlock width="50px" height="28px" borderRadius="6px" /><SkeletonBlock width="70%" height="13px" borderRadius="4px" className="skel-mt-sm" /></div>
        <div className="skel-stat-card"><SkeletonBlock width="50px" height="28px" borderRadius="6px" /><SkeletonBlock width="70%" height="13px" borderRadius="4px" className="skel-mt-sm" /></div>
        <div className="skel-stat-card"><SkeletonBlock width="50px" height="28px" borderRadius="6px" /><SkeletonBlock width="70%" height="13px" borderRadius="4px" className="skel-mt-sm" /></div>
      </div>
    </div>
    <div className="skel-about-right">
      <SkeletonBlock width="100%" height="clamp(280px,40vw,420px)" borderRadius="20px" />
    </div>
  </div>


);

// ═══════════════════════════════════════════════
//   SELLER DASHBOARD PAGE SKELETON
// ═══════════════════════════════════════════════

// ── Seller Dashboard Page ─────────────────────────────
// Header + 4 stat cards + low stock table + (recent orders,
// support tickets) side by side + top products table
export const SellerDashboardSkeleton = () => (
  <div style={{ padding: '0 0.5rem' }}>
    <DashPageHeader />
    <StatsRowSkeleton count={4} />
    <div style={{ marginBottom: '1.5rem' }}>
      <TableCardSkeleton rows={5} cols={7} />
    </div>
    <div className="skel-two-col">
      <CardSectionSkeleton rows={5} />
      <CardSectionSkeleton rows={5} />
    </div>
    <TableCardSkeleton rows={5} cols={5} />
  </div>
);

// ── Customer Dashboard Page ───────────────────────────
// Welcome header + 4 stat cards + (support tickets, recent
// chats) side by side + recent orders table with pagination
export const CustomerDashboardSkeleton = () => (
  <div style={{ padding: '0 0.5rem' }}>
    <DashPageHeader />
    <StatsRowSkeleton count={4} />
    <div className="skel-two-col">
      <CardSectionSkeleton rows={4} />
      <CardSectionSkeleton rows={4} />
    </div>
    <TableCardSkeleton rows={5} cols={6} />
  </div>
);

// ── Admin Dashboard Page ──────────────────────────────
// 4 stat cards + help tickets table (full width) + (sales
// trend, sales by category) + (payouts, blogs) + (top
// orders, top sellers) — mirrors the 5-row admin layout
export const AdminDashboardSkeleton = () => (
  <div style={{ padding: '0 0.5rem' }}>
    <StatsRowSkeleton count={4} />
    <div style={{ marginTop: '1rem', marginBottom: '1.5rem' }}>
      <TableCardSkeleton rows={5} cols={5} />
    </div>
    <div className="skel-admin-dash-row">
      <CardSectionSkeleton rows={5} />
      <CardSectionSkeleton rows={4} />
    </div>
    <div className="skel-admin-dash-row">
      <CardSectionSkeleton rows={5} />
      <CardSectionSkeleton rows={3} />
    </div>
    <div className="skel-two-col" style={{ marginBottom: 0 }}>
      <CardSectionSkeleton rows={5} />
      <CardSectionSkeleton rows={5} />
    </div>
  </div>
);
// ── Sellers Page ──────────────────────────────────────
export const SellersPageSkeleton = () => (
  <div style={{ padding: '0 0.5rem' }}>
    <StatsRowSkeleton count={5} />
    <FilterBarSkeleton />
    <TableCardSkeleton rows={8} cols={7} />
  </div>
);

// ═══════════════════════════════════════════════
//   ADMIN PANEL — REMAINING PAGE SKELETONS
// ═══════════════════════════════════════════════

// ── Blogs List Page Skeleton ───────────────────────────
export const BlogsListSkeleton = () => (
  <div style={{ padding: '0 0.5rem' }}>
    <StatsRowSkeleton count={3} />
    <FilterBarSkeleton />
    <TableCardSkeleton rows={8} cols={7} />
  </div>
);

// ── Categories List Page Skeleton ──────────────────────
export const CategoriesListSkeleton = () => (
  <div style={{ padding: '0 0.5rem' }}>
    <StatsRowSkeleton count={3} />
    <FilterBarSkeleton />
    <TableCardSkeleton rows={8} cols={6} />
  </div>
);

// ── Customers List Page Skeleton ───────────────────────
export const CustomersListSkeleton = () => (
  <div style={{ padding: '0 0.5rem' }}>
    <StatsRowSkeleton count={4} />
    <FilterBarSkeleton />
    <TableCardSkeleton rows={8} cols={9} />
  </div>
);

// ── Delivery Men Page Skeleton ─────────────────────────
export const DeliveryMenSkeleton = () => (
  <div style={{ padding: '0 0.5rem' }}>
    <StatsRowSkeleton count={5} />
    <FilterBarSkeleton />
    <TableCardSkeleton rows={8} cols={9} />
  </div>
);

// ── FAQs Page Skeleton ──────────────────────────────────
export const FAQsSkeleton = () => (
  <div style={{ padding: '0 0.5rem' }}>
    <StatsRowSkeleton count={3} />
    <FilterBarSkeleton />
    <TableCardSkeleton rows={6} cols={5} />
  </div>
);

// ── Orders List (Admin) Page Skeleton ──────────────────
export const OrdersListSkeleton = () => (
  <div style={{ padding: '0 0.5rem' }}>
    <StatsRowSkeleton count={4} />
    <FilterBarSkeleton />
    <TableCardSkeleton rows={8} cols={7} />
  </div>
);

// ── Sales Marquee Page Skeleton ────────────────────────
export const SalesMarqueeSkeleton = () => (
  <div style={{ padding: '0 0.5rem' }}>
    <CardSectionSkeleton rows={2} />
    <TableCardSkeleton rows={5} cols={4} />
  </div>
);

// ── Sub-Admins List Page Skeleton ──────────────────────
export const SubAdminsListSkeleton = () => (
  <div style={{ padding: '0 0.5rem' }}>
    <StatsRowSkeleton count={4} />
    <FilterBarSkeleton />
    <TableCardSkeleton rows={6} cols={5} />
  </div>
);

// ── Delivery Topup Page Skeleton ───────────────────────
export const DeliveryTopupSkeleton = () => (
  <div style={{ padding: '0 0.5rem' }}>
    <StatsRowSkeleton count={4} />
    <FilterBarSkeleton />
    <TableCardSkeleton rows={6} cols={7} />
  </div>
);

// ── Products List (Admin) Page Skeleton ────────────────
export const ProductsListSkeleton = () => (
  <div style={{ padding: '0 0.5rem' }}>
    <StatsRowSkeleton count={4} />
    <FilterBarSkeleton />
    <TableCardSkeleton rows={8} cols={10} />
  </div>
);

// ═══════════════════════════════════════════════
//   DELIVERY PANEL PAGE SKELETONS
// ═══════════════════════════════════════════════

// ── Delivery Support Page ────────────────────────────
export const DeliverySupportPageSkeleton = () => (
  <div style={{ padding: '0 0.5rem' }}>
    <DashPageHeader />
    <div style={{ marginBottom: '1.5rem' }}>
      <TableCardSkeleton rows={4} cols={7} />
    </div>
    <div style={{ marginBottom: '1.5rem' }}>
      <TableCardSkeleton rows={3} cols={6} />
    </div>
    <div className="skel-two-col" style={{ marginBottom: 0 }}>
      <FaqSkeleton />
      <FormCardSkeleton fields={4} />
    </div>
  </div>
);

// ── My Jobs Page (Delivery) ───────────────────────────
// Header + status tabs row + job cards (pickup/dropoff/products/action)
export const MyJobsPageSkeleton = () => (
  <div style={{ padding: '0 0.5rem' }}>
    <DashPageHeader />
    {/* Status tabs */}
    <div style={{ display: 'flex', gap: '8px', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
      {[1,2,3,4,5].map(i => <SkeletonBlock key={i} width="100px" height="34px" borderRadius="8px" />)}
    </div>
    {/* Job cards */}
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} style={{ background: '#fff', borderRadius: '12px', padding: '1.25rem', boxShadow: '0 2px 10px rgba(0,0,0,.06)' }}>
          <SkeletonBlock width="140px" height="14px" borderRadius="4px" />
          <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.85rem' }}>
            <SkeletonBlock width="70px" height="20px" borderRadius="6px" />
            <SkeletonBlock width="55%" height="14px" borderRadius="4px" />
          </div>
          <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.6rem' }}>
            <SkeletonBlock width="70px" height="20px" borderRadius="6px" />
            <SkeletonBlock width="45%" height="14px" borderRadius="4px" />
          </div>
          <SkeletonBlock width="100%" height="1px" borderRadius="0" className="skel-mt" />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.85rem' }}>
            <SkeletonBlock width="120px" height="14px" borderRadius="4px" />
            <SkeletonBlock width="130px" height="36px" borderRadius="8px" />
          </div>
        </div>
      ))}
    </div>
  </div>
);

// ── Earnings & Wallet Page (Delivery) ─────────────────
// Header + balance/earned/deliveries/deductions stat row +
// (topup panel, account details) + earnings table
export const EarningsWalletPageSkeleton = () => (
  <div style={{ padding: '0 0.5rem' }}>
    <DashPageHeader />
    <StatsRowSkeleton count={4} />
    <div className="skel-two-col">
      <CardSectionSkeleton rows={3} />
      <CardSectionSkeleton rows={3} />
    </div>
    <TableCardSkeleton rows={5} cols={5} />
  </div>
);

// ═══════════════════════════════════════════════
//   SELLER PANEL PAGE SKELETONS
// ═══════════════════════════════════════════════

// ── Seller Earnings Page ──────────────────────────────
// Header + 4 stat cards + commission banner + transactions table
export const SellerEarningsPageSkeleton = () => (
  <div style={{ padding: '0 0.5rem' }}>
    <DashPageHeader />
    <StatsRowSkeleton count={4} />
    <div style={{ marginBottom: '1.5rem' }}>
      <CardSectionSkeleton rows={1} />
    </div>
    <FilterBarSkeleton />
    <TableCardSkeleton rows={8} cols={8} />
  </div>
);

// ── Seller Orders Page ────────────────────────────────
// Header + 4 stat cards + tabs row + filter bar + orders table
export const SellerOrdersPageSkeleton = () => (
  <div style={{ padding: '0 0.5rem' }}>
    <DashPageHeader />
    <StatsRowSkeleton count={4} />
    <div style={{ display: 'flex', gap: '8px', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
      {[1, 2, 3].map(i => <SkeletonBlock key={i} width="140px" height="38px" borderRadius="8px" />)}
    </div>
    <FilterBarSkeleton />
    <TableCardSkeleton rows={8} cols={8} />
  </div>
);

// ── Seller Products Page ──────────────────────────────
// Header + search bar + products table
export const SellerProductsPageSkeleton = () => (
  <div style={{ padding: '0 0.5rem' }}>
    <DashPageHeader />
    <FilterBarSkeleton />
    <TableCardSkeleton rows={8} cols={11} />
  </div>
);

// ── Seller Reviews Page ───────────────────────────────
// Header + 3 stat cards + filter bar + reviews table
export const SellerReviewsPageSkeleton = () => (
  <div style={{ padding: '0 0.5rem' }}>
    <DashPageHeader />
    <StatsRowSkeleton count={3} />
    <FilterBarSkeleton />
    <TableCardSkeleton rows={6} cols={6} />
  </div>
);

// ── Seller Support Page ───────────────────────────────
// Header + 2 tables (received/my tickets) + (FAQ, create-ticket form)
export const SellerSupportPageSkeleton = () => (
  <div style={{ padding: '0 0.5rem' }}>
    <DashPageHeader />
    <div style={{ marginBottom: '1.5rem' }}>
      <TableCardSkeleton rows={4} cols={8} />
    </div>
    <div style={{ marginBottom: '1.5rem' }}>
      <TableCardSkeleton rows={3} cols={6} />
    </div>
    <div className="skel-two-col" style={{ marginBottom: 0 }}>
      <FaqSkeleton />
      <FormCardSkeleton fields={3} />
    </div>
  </div>
);

// ── Seller Settings Page ──────────────────────────────
// Header + tabs row + form card (shop profile / personal info shape)
export const SellerSettingsPageSkeleton = () => (
  <div style={{ padding: '0 0.5rem' }}>
    <DashPageHeader />
    <div style={{ display: 'flex', gap: '8px', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
      {[1, 2, 3, 4, 5, 6].map(i => <SkeletonBlock key={i} width="150px" height="42px" borderRadius="8px" />)}
    </div>
    <FormCardSkeleton fields={6} />
  </div>
);

export default SkeletonBlock;
